import { useState, useEffect, useCallback, useMemo, useRef, type Ref, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import ShopSelector from '@/components/selectors/ShopSelector';
import ModuleSelector from '@/components/selectors/ModuleSelector';
import DateRangeSelector from '@/components/selectors/DateRangeSelector';
import { ModuleType } from './types';
import { useStore } from '@core/store';
import { shallow } from 'zustand/shallow';
import { useShopPersistence } from '@/components/shared/navigation/securePage';
import { FilterRef } from '@features/statistics/types.ts';

export interface FilterModuleDateRangeProps {
    forType: string;
    onLoadData?: (data: ModuleDateRangeData) => void;
    ref?: Ref<FilterRef>;
}

export interface ModuleDateRangeData {
    selectedShopId: string;
    start: string;
    end: string;
    deviceId: string;
    moduleId: string;
}

const FilterModuleDateRange = ({ forType, onLoadData, ref }: FilterModuleDateRangeProps) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [moduleId, setModuleId] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string>(DateTime.local().minus({ days: 7 }).toISODate() || '');
    const [endDate, setEndDate] = useState<string>(DateTime.local().toISODate() || '');

    const hasInitialized = useRef(false);

    // Get module data from store
    const {
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    } = useStore(
        (state) => ({
            trolleyShelterIds: state.trolleyShelterIds,
            zoneTrackerIds: state.zoneTrackerIds,
            lineTrackerIds: state.lineTrackerIds,
            loadTrolleyShelterIds: state.loadTrolleyShelterIds,
            loadZoneTrackerIds: state.loadZoneTrackerIds,
            loadLineTrackerIds: state.loadLineTrackerIds
        }),
        shallow
    );

    // Load module data
    useEffect(() => {
        const loadModuleData = async () => {
            const normalizedType = forType.toLowerCase();

            if (normalizedType.includes('trolleyshelter') && (!trolleyShelterIds || trolleyShelterIds.length === 0)) {
                await loadTrolleyShelterIds();
            } else if (normalizedType.includes('zonetracker') && (!zoneTrackerIds || zoneTrackerIds.length === 0)) {
                await loadZoneTrackerIds();
            } else if (normalizedType.includes('linetracker') && (!lineTrackerIds || lineTrackerIds.length === 0)) {
                await loadLineTrackerIds();
            }
        };

        loadModuleData();
    }, [
        forType,
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    ]);

    useEffect(() => {
        emitLoadData();
    }, [selectedShopIds[0], moduleId, startDate, endDate]);

    // Filter modules
    const filteredModules = useMemo(() => {
        if (!selectedShopIds[0]) return [];

        const normalizedType = forType.toLowerCase();
        let moduleList: any[] = [];

        if (normalizedType.includes('trolleyshelter')) {
            moduleList = trolleyShelterIds;
        } else if (normalizedType.includes('zonetracker')) {
            moduleList = zoneTrackerIds;
        } else if (normalizedType.includes('linetracker')) {
            moduleList = lineTrackerIds;
        }

        return moduleList.filter(
            (module) => module.shopId === selectedShopIds[0] && !module.disabled && !module.deleteDate
        );
    }, [selectedShopIds, forType, trolleyShelterIds, zoneTrackerIds, lineTrackerIds]);

    // Auto-select first module when filtered modules change
    useEffect(() => {
        if (filteredModules.length > 0 && !moduleId) {
            setModuleId(filteredModules[0].id);
        } else if (filteredModules.length === 0 && moduleId) {
            // Clear selection if no modules available
            setModuleId(null);
        }
    }, [filteredModules, moduleId]);

    const selectedModule = useMemo(() => {
        return filteredModules.find((m) => m.id === moduleId) || null;
    }, [filteredModules, moduleId]);

    const errorMessages = useMemo(() => {
        if (!startDate || !endDate) return [];

        const start = DateTime.fromISO(startDate, { zone: 'UTC' });
        const end = DateTime.fromISO(endDate, { zone: 'UTC' });

        if (!start.isValid || !end.isValid) return [];

        const daysDiff = Math.abs(end.diff(start, 'days').days);
        return daysDiff <= 30 ? [] : [t('errors.rangeLtDays', { days: 30 }) || 'Range must be less than 30 days'];
    }, [startDate, endDate, t]);

    // Handle route query parameters - Only run once
    useEffect(() => {
        if (hasInitialized.current) return;

        const params = new URLSearchParams(location.search);
        if (params.get('selectedShopId ')) setSelectedShopIds([params.get('selectedShopId ')!]);
        if (params.get('moduleId')) setModuleId(params.get('moduleId'));

        const startParam = params.get('start');
        const endParam = params.get('end');

        if (startParam) {
            const parsed = DateTime.fromISO(startParam, { zone: 'UTC' });
            if (parsed.isValid) {
                const isoDate = parsed.toISODate();
                if (isoDate) setStartDate(isoDate);
            }
        }
        if (endParam) {
            const parsed = DateTime.fromISO(endParam, { zone: 'UTC' });
            if (parsed.isValid) {
                const isoDate = parsed.toISODate();
                if (isoDate) setEndDate(isoDate);
            }
        }

        hasInitialized.current = true;
    }, []);

    // Stable callbacks
    const handleShopChange = useCallback((value: string | null) => {
        setSelectedShopIds(value ? [value] : []);
        setModuleId(null); // Reset module when shop changes, auto-select will handle it
    }, []);

    const handleModuleChange = useCallback((value: string | null) => {
        setModuleId(value);
    }, []);

    const handleStartDateChange = useCallback((date: string) => {
        setStartDate(date);
    }, []);

    const handleEndDateChange = useCallback((date: string) => {
        setEndDate(date);
    }, []);

    const emitLoadData = useCallback(() => {
        if (!selectedModule || !selectedShopIds[0]) return;

        const start = DateTime.fromISO(startDate, { zone: 'UTC' }).startOf('day').toISO();
        const end = DateTime.fromISO(endDate, { zone: 'UTC' }).endOf('day').toISO();

        if (!start || !end) return;

        onLoadData?.({
            selectedShopId: selectedShopIds[0],
            start,
            end,
            deviceId: selectedModule.deviceId,
            moduleId: selectedModule.id
        });
    }, [selectedModule, selectedShopIds, startDate, endDate]);

    useImperativeHandle(ref, () => ({ refresh: () => emitLoadData() }), [emitLoadData]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2.9,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    mb: 6,
                    mt: 1
                }}
            >
                <Box sx={{ flex: '0 0 auto' }}>
                    <ShopSelector value={selectedShopIds[0] ?? null} onChange={handleShopChange} label={t('shop')} />
                </Box>

                <Box sx={{ width: 200, flex: '0 0 auto' }}>
                    <ModuleSelector
                        value={moduleId}
                        onChange={handleModuleChange}
                        shopId={selectedShopIds[0] ?? null}
                        moduleType={forType as ModuleType}
                        label={t(forType)}
                        modules={filteredModules}
                    />
                </Box>

                <DateRangeSelector
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    maxRange={30}
                    locale={i18n.language}
                    error={errorMessages.length > 0}
                    helperText={errorMessages.length > 0 ? errorMessages[0] : undefined}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default FilterModuleDateRange;
